/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useState, useRef } from "react";
import Filter from "../../components/Filter";
import Graph from "./graph";
import { useEffect } from "react";
import fetchHelper from "../../services/fetchHelper";
import { useContext } from "react";
import { LayoutContext } from "../../components/layout";
import useError from "../../hooks/useError";

const loadingIco = require("../../assets/esign-ui-icons/saving-ico.svg");
const checkIco = require("../../assets/esign-ui-icons/ico-check.svg");
const closeIco = require("../../assets/esign-ui-icons/ico-close.svg");

const LocationEdit = ({ data, locationJsonData, onChange, onChangeJsonData, currLocation = {} }) => {
  const locData = locationJsonData[data.value] || {};
  const [loading, setLoading] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [success, setSuccess] = useState(null);
  const [m30, setM30] = useState(locData.m30 || 0);
  const [m90, setM90] = useState(locData.m90 || 0);
  const showDefaultApiError = useError();

  useEffect(() => {
    if (success !== null) {
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    }
  }, [success]);

  const save = async () => {
    setLoading(true);
    try {
      // data
      let url = "/patientmanagement/rest/location/" + currLocation.id;
      const body = { ...locationJsonData, [data.value]: { m30: m30, m90: m90 } };
      const resData = await fetchHelper.put(url, { id: currLocation.id, jsonData: JSON.stringify(body) });
      console.log(resData);
      setSuccess(true);
      onChange({ m30, m90 });
      onChangeJsonData(body);
    } catch (err) {
      showDefaultApiError(err);
      setSuccess(false);
      throw err;
    }
    setLoading(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "row" }}>
      <div style={{ marginRight: 5 }}>
        {loading || typeof success === "boolean" ? (
          <img id="loading-spinner" src={loading ? loadingIco : success ? checkIco : closeIco} style={{ width: 15, height: 15 }} />
        ) : (
          <a
            href="#"
            onClick={async (e) => {
              e.preventDefault();
              try {
                if (isEditable) await save();
                setIsEditable(!isEditable);
              } catch (err) { }
            }}
          >
            {isEditable ? "save" : "edit"}
          </a>
        )}
      </div>
      <div style={{ display: "flex", justifyContent: "center" }}>
        {!isEditable ? (
          `$${m30}`
        ) : (
          <>
            $
            <input
              className="location-input"
              style={{ width: (m30 + "").length * 10 + 5 }}
              value={m30}
              onChange={(e) => setM30(e.target.value)}
            />
          </>
        )}
        <span style={{ margin: "0 5px" }}>/</span>
        {!isEditable ? (
          `$${m90}`
        ) : (
          <>
            $
            <input
              className="location-input"
              style={{ width: (m90 + "").length * 10 + 5 }}
              value={m90}
              onChange={(e) => setM90(e.target.value)}
            />
          </>
        )}
      </div>
    </div>
  );
};

const LocationGrahp = () => {
  const { user } = useContext(LayoutContext);
  let isInit = useRef(false).current;
  const isDev = false;
  const [locationOptions, setlocationOptions] = useState([]);
  const [locationFilter, setLocationFilter] = useState({});
  const { currentLocation: currLocation } = useContext(LayoutContext);
  const [filter, setFilter] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [locationJsonData, setLocationJsonData] = useState({});
  const interrogationStatusOptions = [
    { value: "PRELIMINARY", label: "PRELIMINARY" },
    { value: "TERMINATED", label: "REMOVED" },
    { value: "FINAL", label: "FINAL" },
    { value: "NOTASSIGNED", label: "NOTASSIGNED" },
  ];
  const dayOptions = [
    { value: "ALL", label: "All" },
    { value: "NONE", label: "None" },
    { value: "SERVICE", label: "Service" },
    { value: "SERVICE_NON_DUAL", label: "Service Not-Dual" },
  ];

  useEffect(() => {
    if (!isInit && user && user.locations) {
      isInit = true;
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      let url = "/patientmanagement/rest/location/serviceReport";
      const res = await fetchHelper.get({ url });

      setLocationJsonData(JSON.parse(currLocation.jsonData || "{}"));
      setLocationFilter(JSON.parse(currLocation.jsonData || "{}"));
      setlocationOptions(res.map((i) => ({ value: i.id, label: i.name })));
    } catch (err) {
      console.log(err);
      setlocationOptions([{ value: user?.locations[0]?.id, label: user?.locations[0]?.name }]);
    } finally {
      setIsLoading(false);
    }
  };

  const renderRightLocationComponent = (data) => {
    return (
      <LocationEdit
        data={data}
        locationJsonData={locationJsonData}
        onChange={(r) => {
          setLocationFilter({ ...locationFilter, [data.value]: r });
        }}
        onChangeJsonData={(jsonData) => setLocationJsonData(jsonData)}
        currLocation={currLocation}
      />
    );
  };

  return (
    <div style={{ display: "flex", flex: 1, flexDirection: "row" }}>
      <div style={{ display: "flex", width: 350 }}>
        {locationOptions.length > 0 ? (
          <Filter
            loading={isLoading}
            fields={[
              { type: "line" },
              {
                type: "dateRange",
                key: "dateRange",
                initialStart: "02/01/2019",
                initialEnd: "02/28/2021",
                mode: "dropdown",
              },
              {
                type: "multiSelect",
                whereKey: "interrogationStatus",
                key: "interrogationStatus",
                label: "Interrogation Status",
                options: interrogationStatusOptions,
                initialSelected: [{ value: "FINAL" }, { value: "PRELIMINARY" }, { value: "NOTASSIGNED" }],
              },
              {
                type: "multiSelect",
                key: "m30Filter",
                label: "30Day",
                options: dayOptions,
                maxSelect: 1,
                initialSelected: [{ value: "SERVICE" }],
              },
              {
                type: "multiSelect",
                key: "m90Filter",
                label: "90Day",
                options: dayOptions,
                maxSelect: 1,
                initialSelected: [{ value: "SERVICE_NON_DUAL" }],
              },
              {
                type: "multiSelect",
                whereKey: "location.id",
                key: "location",
                label: "Location",
                options: locationOptions,
                forceHasSelectAll: true,
                initialSelected: isDev
                  ? [{ value: "136" }, { value: "44" }, { value: "112" }, { value: "41" }]
                  : [{ value: user?.locations[0]?.id }],
                rightComponent: renderRightLocationComponent,
              },
            ]}
            onChange={(data) => setFilter(data)}
          />
        ) : null}
      </div>
      <div
        style={{ display: "flex", height: "auto", flexDirection: "column", flex: 1, paddingLeft: 10 }}
        className="filter-container"
      >
        <div className="filter-content-container">
          <Graph filter={filter} isLoading={(data) => setIsLoading(data)} locationFilter={locationFilter} />
        </div>
      </div>
    </div>
  );
};

export default LocationGrahp;
